import type { CrmLinkDiscountMode } from '../../discountMode.utils'
import {
  getCrmLinkPriceNowFromBase,
  resolveCrmLinkDiscountMode,
} from '../../discountMode.utils'

export type MonthlyCourseLinkPeriodUnit = 'month' | 'day'

export const DEFAULT_MONTHLY_COURSE_PERIOD_UNIT: MonthlyCourseLinkPeriodUnit =
  'month'

/** Промежуточное округление до копеек перед Math.round в целые. */
const round2 = (value: number) => Math.round(value * 100) / 100

/**
 * Сумма в `price` / «Итого к оплате».
 * Пока колонка в БД — decimal(10,0), бэк сохранит целые рубли.
 * После decimal(10,2) заменить на round2.
 */
export const roundCrmLinkStoredPrice = (value: number) =>
  Math.round(Math.max(0, Number(value) || 0))

const getPositiveInt = (value: unknown, fallback = 1) => {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue < 1) return fallback
  return Math.trunc(numericValue)
}

const getDaysInMonth = (date?: string | null) => {
  const parsedDate = date ? new Date(`${date}T00:00:00`) : new Date()
  const year = Number.isNaN(parsedDate.getTime())
    ? new Date().getFullYear()
    : parsedDate.getFullYear()
  const month = Number.isNaN(parsedDate.getTime())
    ? new Date().getMonth()
    : parsedDate.getMonth()

  return new Date(year, month + 1, 0).getDate()
}

export const getMonthlyCourseLinkBasePrice = (firstPrice: unknown) =>
  Number(firstPrice) || 0

export const getMonthlyCourseLinkDaysInMonth = getDaysInMonth

export const getMonthlyCourseLinkMonthUnitPrice = (
  tariff: ICourseTariff | null | undefined,
  value: unknown,
) => {
  const firstPrice = getMonthlyCourseLinkBasePrice(tariff?.first_price)
  const periodValue = getPositiveInt(value)

  if (periodValue <= 2) return firstPrice
  if (periodValue <= 4) {
    return getMonthlyCourseLinkBasePrice(tariff?.second_price ?? firstPrice)
  }

  return getMonthlyCourseLinkBasePrice(tariff?.third_price ?? firstPrice)
}

export const getMonthlyCourseLinkPrice = (
  firstPrice: unknown,
  discount: unknown,
) => {
  const basePrice = getMonthlyCourseLinkBasePrice(firstPrice)
  const discountValue = Number(discount) || 0
  return Math.max(0, basePrice - discountValue)
}

export const getMonthlyCourseLinkPeriodBasePrice = (
  tariff: ICourseTariff | null | undefined,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  enrollDate?: string | null,
) => {
  const firstPrice = getMonthlyCourseLinkBasePrice(tariff?.first_price)
  const periodValue = getPositiveInt(value)

  if (periodUnit === 'day') {
    const daysInMonth = getMonthlyCourseLinkDaysInMonth(enrollDate)
    return round2((firstPrice / daysInMonth) * periodValue)
  }

  return round2(
    getMonthlyCourseLinkMonthUnitPrice(tariff, periodValue) * periodValue,
  )
}

export const getClassLinkBasePrice = (amount: unknown) => Number(amount) || 0

export const getClassLinkPeriodBasePrice = (
  amount: unknown,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  enrollDate?: string | null,
) => {
  const basePrice = getClassLinkBasePrice(amount)
  const periodValue = getPositiveInt(value)

  if (periodUnit === 'day') {
    const daysInMonth = getMonthlyCourseLinkDaysInMonth(enrollDate)
    return round2((basePrice / daysInMonth) * periodValue)
  }

  return round2(basePrice * periodValue)
}

/** Скидка на пакет: один раз, либо ×N мес. при apply_discount_each_payment. */
export const getLinkPeriodDiscountTotal = (
  discount: unknown,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  applyDiscountEachPayment?: boolean | null,
) => {
  const discountValue = Number(discount) || 0
  if (discountValue <= 0) return 0

  if (applyDiscountEachPayment && periodUnit === 'month') {
    return round2(discountValue * getPositiveInt(value))
  }

  return discountValue
}

/** Промежуточный расчёт до округления в целые рубли. */
const getClassLinkCalculatedPrice = (
  amount: unknown,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  discount: unknown,
  enrollDate?: string | null,
  applyDiscountEachPayment?: boolean | null,
  discountMode?: CrmLinkDiscountMode | null,
  isPeriodFixed?: boolean,
) => {
  const basePrice = getClassLinkPeriodBasePrice(
    amount,
    periodUnit,
    value,
    enrollDate,
  )
  const mode = resolveCrmLinkDiscountMode({
    discount: Number(discount) || 0,
    discountMode,
    applyDiscountEachPayment,
    isPeriodFixed,
  })
  const discountForNow =
    mode === 'every_payment'
      ? getLinkPeriodDiscountTotal(discount, periodUnit, value, true)
      : discount
  return getCrmLinkPriceNowFromBase(basePrice, discountForNow, mode)
}

/** Цена для `price` / формы — целые рубли, как сохранит бэк. */
export const getClassLinkSuggestedPrice = (
  amount: unknown,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  discount: unknown,
  enrollDate?: string | null,
  applyDiscountEachPayment?: boolean | null,
  discountMode?: CrmLinkDiscountMode | null,
  isPeriodFixed?: boolean,
) =>
  roundCrmLinkStoredPrice(
    getClassLinkCalculatedPrice(
      amount,
      periodUnit,
      value,
      discount,
      enrollDate,
      applyDiscountEachPayment,
      discountMode,
      isPeriodFixed,
    ),
  )

/** Промежуточный расчёт до округления в целые рубли. */
const getMonthlyCourseLinkCalculatedPrice = (
  tariff: ICourseTariff | null | undefined,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  discount: unknown,
  enrollDate?: string | null,
  applyDiscountEachPayment?: boolean | null,
  discountMode?: CrmLinkDiscountMode | null,
  isPeriodFixed?: boolean,
) => {
  const basePrice = getMonthlyCourseLinkPeriodBasePrice(
    tariff,
    periodUnit,
    value,
    enrollDate,
  )
  const mode = resolveCrmLinkDiscountMode({
    discount: Number(discount) || 0,
    discountMode,
    applyDiscountEachPayment,
    isPeriodFixed,
  })
  // every_payment + месяц: скидка ×N периодов (как getLinkPeriodDiscountTotal)
  const discountForNow =
    mode === 'every_payment'
      ? getLinkPeriodDiscountTotal(discount, periodUnit, value, true)
      : discount
  return getCrmLinkPriceNowFromBase(basePrice, discountForNow, mode)
}

/** Цена для `price` / формы — целые рубли, как сохранит бэк. */
export const getMonthlyCourseLinkSuggestedPrice = (
  tariff: ICourseTariff | null | undefined,
  periodUnit: MonthlyCourseLinkPeriodUnit,
  value: unknown,
  discount: unknown,
  enrollDate?: string | null,
  applyDiscountEachPayment?: boolean | null,
  discountMode?: CrmLinkDiscountMode | null,
  isPeriodFixed?: boolean,
) =>
  roundCrmLinkStoredPrice(
    getMonthlyCourseLinkCalculatedPrice(
      tariff,
      periodUnit,
      value,
      discount,
      enrollDate,
      applyDiscountEachPayment,
      discountMode,
      isPeriodFixed,
    ),
  )

export const getMonthlyCourseLinkPeriodLabel = (
  periodUnit?: string | null,
  value?: number | null,
) => {
  const periodValue = getPositiveInt(value)

  if (periodUnit === 'day') {
    return `на ${periodValue} дн.`
  }

  return `на ${periodValue} мес.`
}

export const isMonthlyCourseLinkTariff = (tariffType?: string | null) =>
  tariffType === 'month'

export const normalizeMonthlyPeriodUnit = (
  periodUnit: unknown,
): MonthlyCourseLinkPeriodUnit =>
  periodUnit === 'day' ? 'day' : DEFAULT_MONTHLY_COURSE_PERIOD_UNIT

export const normalizeMonthlyPeriodValue = (value: unknown) =>
  Math.max(1, Math.trunc(Number(value) || 1))

/** Значения формы при toggle «Зафиксировать период». */
export const getMonthlyPeriodToggleResetValues = (value: unknown) => ({
  value: normalizeMonthlyPeriodValue(value),
  period_unit: DEFAULT_MONTHLY_COURSE_PERIOD_UNIT,
})

export const isMonthlyDiscountInvalid = (
  firstMonthBasePrice: unknown,
  discount: unknown,
) => {
  const discountValue = Number(discount) || 0
  const basePrice = getMonthlyCourseLinkBasePrice(firstMonthBasePrice)
  return discountValue > 0 && discountValue >= basePrice
}

export const isClassDiscountInvalid = (
  classAmount: unknown,
  discount: unknown,
) => {
  const discountValue = Number(discount) || 0
  const basePrice = getClassLinkBasePrice(classAmount)
  return discountValue > 0 && discountValue >= basePrice
}

export const isMonthlyAdditionalCourseDiscountInvalid = (
  item: { discount?: number | null },
  tariff: ICourseTariff | null | undefined,
) => {
  if (!isMonthlyCourseLinkTariff(tariff?.type)) return false
  const discount = Number(item.discount) || 0
  if (discount <= 0) return false
  return discount >= getMonthlyCourseLinkBasePrice(tariff?.first_price)
}

/** period_unit на submit: зафиксирован → month|day, иначе null; не month/class → undefined. */
export const resolveMonthlyPeriodUnitForSubmit = (
  isMonthlyOrClassLink: boolean,
  isPeriodFixed: boolean,
  periodUnit: unknown,
): MonthlyCourseLinkPeriodUnit | null | undefined => {
  if (!isMonthlyOrClassLink) return undefined
  if (!isPeriodFixed) return null
  return normalizeMonthlyPeriodUnit(periodUnit)
}

export const resolveMonthlyMainSubmitPrice = (
  isMonthlyOrClassLink: boolean,
  price: unknown,
) => (isMonthlyOrClassLink ? Math.max(0, Number(price) || 0) : null)

export const resolveMonthlyApplyDiscountEachPayment = (
  isMonthlyOrClassLink: boolean,
  discount: unknown,
  applyDiscountEachPayment: unknown,
) =>
  isMonthlyOrClassLink
    ? Number(discount) > 0 && Boolean(applyDiscountEachPayment)
    : undefined

/** Маппинг доп. курса с month-фиксацией (is_period_fixed / period_unit). */
export const mapMonthlyAdditionalCourseForSubmit = <T extends object>(
  item: T & {
    value?: number | null
    period_unit?: 'month' | 'day' | null
    is_period_fixed?: boolean | null
    discount?: number | null
    discount_mode?: CrmLinkDiscountMode | null
    apply_discount_each_payment?: boolean | null
  },
): T & {
  value: number
  period_unit: MonthlyCourseLinkPeriodUnit | null
  is_period_fixed?: boolean | null
  discount?: number | null
  discount_mode?: CrmLinkDiscountMode | null
  apply_discount_each_payment?: boolean | null
} => {
  const nextValue = normalizeMonthlyPeriodValue(item.value)
  const discount = Number(item.discount) || 0
  const isPeriodFixed = Boolean(item.is_period_fixed)
  const mode =
    discount > 0
      ? resolveCrmLinkDiscountMode({
          discount,
          discountMode: item.discount_mode,
          applyDiscountEachPayment: item.apply_discount_each_payment,
          isPeriodFixed,
        })
      : null

  if (isPeriodFixed) {
    return {
      ...item,
      value: nextValue,
      period_unit: normalizeMonthlyPeriodUnit(item.period_unit),
      discount: discount > 0 ? discount : null,
      discount_mode: mode,
      apply_discount_each_payment: mode === 'every_payment',
    }
  }

  return {
    ...item,
    value: nextValue,
    period_unit: null,
    is_period_fixed: false,
    discount: discount > 0 ? discount : null,
    discount_mode: mode,
    apply_discount_each_payment: mode === 'every_payment',
  }
}
